import { ComponentProps, ReactNode } from 'react';

import OtaClient from '@crowdin/ota-client';
import { getWithDefault } from '@leobastiani/get-with-default';
import memoizee from '@leobastiani/memoizee';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { createIntlAtom } from '@wellhub/react-intl-crowdin-jotai';
// eslint-disable-next-line import/no-unresolved
import { matchLanguage } from '@wellhub/react-intl-crowdin-jotai/match-language';
import enGB from 'assets/translations/en-GB/translations.json';
import ptBR from 'assets/translations/pt-BR/translations.json';
import {
  ptBR as ptBRLocale,
  enGB as enGBLocale,
  enUS as enUSLocale,
  it as itLocale,
} from 'date-fns/locale';
import { atom, getDefaultStore, useAtomValue } from 'jotai';
import { createIntl, IntlProvider, RawIntlProvider } from 'react-intl';

const defaultLang = 'en-GB';

const defaultRichTextElements: ComponentProps<
  typeof IntlProvider
>['defaultRichTextElements'] = {
  b: chunks => <b>{chunks}</b>,
};

export const { intlAtom } = (() => {
  if (typeof jest !== 'undefined') {
    // will be used in jest
    return {
      intlAtom: atom(() =>
        Object.assign(
          createIntl({
            locale: 'pt-BR',
            defaultLocale: 'pt-BR',
            messages: {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onError() {},
          }),
          {
            matchedLanguage: 'pt-BR',
          },
        ),
      ),
    } as never;
  }
  return createIntlAtom({
    crowdinHash: 'e-ba96ab9c94cba146dd9f3b3clw',
    defaultLang,
    memoizee,
    OtaClient,
    timeout: 5000,
    messages: {
      'en-GB': enGB,
      'pt-BR': ptBR,
      it: async () =>
        (await import('assets/translations/it/translations.json')).default,
    },
    createIntlOptions: {
      defaultRichTextElements,
    },
  });
})();

const dateFnsLocales = {
  'en-US': enUSLocale,
  'en-GB': enGBLocale,
  'pt-BR': ptBRLocale,
  it: itLocale,
};

export const MyIntlProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const intl = useAtomValue(intlAtom);
  const matchedLanguage = matchLanguage(
    intl.locale,
    Object.keys(dateFnsLocales),
  );

  return (
    <RawIntlProvider value={intl}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={
          matchedLanguage
            ? getWithDefault(
                dateFnsLocales,
                matchedLanguage,
                dateFnsLocales[defaultLang],
              )
            : dateFnsLocales[defaultLang]
        }
      >
        {children}
      </LocalizationProvider>
    </RawIntlProvider>
  );
};

if (typeof jest === 'undefined') {
  getDefaultStore().sub(intlAtom, () => {
    document.documentElement.setAttribute(
      'lang',
      matchLanguage(
        getDefaultStore().get(intlAtom).locale,
        Object.keys(dateFnsLocales),
      ) ?? defaultLang,
    );
  });
}
